import React, { useState, useEffect } from "react";
import { Circle } from 'lucide-react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import LoanApplicationForm from "./LoanApplicationForm";
import vendorService from "@/modules/vendor/service/vendor.int.service";
import { Assignment as AssignmentIcon } from '@mui/icons-material';
import ToastService from "@/service/toaster.service";
import { useSelector } from "react-redux";
import "react-toastify/dist/ReactToastify.css"; // Make sure to import CSS
import { ToastContainer } from "react-toastify";
import chatService from "@/service/chat.service";

const statusOptions = [
  { value: "new", label: "New", color: "#6B7280" },
  { value: "in_progress", label: "In Progress", color: "#3B82F6" },
  { value: "awaiting_documents", label: "Awaiting Documents", color: "#EF4444" },
  { value: "completed", label: "Completed", color: "#22C55E" },
  { value: "pending", label: "Pending", color: "#EAB308" },
];

const LoanApplications = () => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [applications, setApplications] = useState([]); // Store API response
  const [loading, setLoading] = useState(true); // Add a loading state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.auth);

  useEffect(() => {
    fetchApplications();
    checkTwoFactorStatus();
  }, [user]);

  const checkTwoFactorStatus = async () => {
      try {
        const response = await chatService.checkTwoFactorStatus('vendor');
        setTwoFactorEnabled(response.is_enabled);
    } catch (error) {
      console.error('Error checking 2FA status:', error);
      setTwoFactorEnabled(false);
    }
  };

  const fetchApplications = async () => {
    setLoading(true); // Start loading
    try {
      if (!user.id) {
        console.error(user, "Vendor ID not found ");
        return;
      }
      const response = await vendorService.getLoanApplications(user.id);
      setApplications(response.data.data);
    } catch (error) {
      console.error("Error fetching loan applications:", error);
    } finally {
      // fetchApplications();

      setLoading(false); // Stop loading
    }
  };

  // const handleTabChange = (event, newValue) => {
  //   setTabValue(newValue);
  // };

  const filteredApplications =
    tabValue === 0
      ? applications
      : applications.filter((app) => {
        if (tabValue === 1) return app.status === "pending";
        if (tabValue === 2) return app.status === "in-progress";
        if (tabValue === 3) return app.status === "approved";
        if (tabValue === 4) return app.status === "rejected";
        return true;
      });



  const handleReviewClick = (application) => {
    setSelectedApplication(application);
    setOpenModal(true);
  };

  const formatDatePart = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatTimePart = (dateString) => {
    const date = new Date(dateString);
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    return `${hours % 12 || 12}:${minutes} ${ampm}`;
  };

  const updateApplicationStatus = async (applicationId, newStatus) => {
    try {
      const response = await vendorService.updateLoanApplicationStatus(applicationId, newStatus);
      if (response.status == true) {
        setApplications((prevApplications) =>
          prevApplications.map((app) =>
            app._id === applicationId ? { ...app, status: newStatus } : app
          )
        );
        ToastService.success("Application status Updated!");
      }
    } catch (error) {
      ToastService.error("failed!");
      console.error("Error updating status:", error);
    }
  };

  return (
    <>
      <Box>
        <ToastContainer />
        <Typography variant="h4" component="h1" gutterBottom color="primary">
          Review lead Applications
        </Typography>

        <Card sx={{
          mb: 0, borderRadius: 5,
          border: "1px solid #cdccf0",
        }}>
          <CardContent>
            {/* Show loader while data is being fetched */}
            {loading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{ height: '200px' }} // Adjust the height based on your design
              >
                <CircularProgress />
              </Box>
            ) : (
              <Box>
                {/* Conditional rendering for applications or no data message */}
                {applications.length === 0 ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ height: '100%', textAlign: 'center' }}
                  >
                    <AssignmentIcon sx={{ m: 8, fontSize: 100, color: 'grey.500' }} />
                    <Typography variant="body1" color="text.secondary">
                      No applications available.
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer
                    sx={{
                      // mt: 2,
                      maxHeight: "700px", // Set a max height for the scrollable area
                      overflow: "auto", // Enable scrolling when content overflows
                      "&::-webkit-scrollbar": {
                        display: "none", // Hide the scrollbar
                      },
                    }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Applicant</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Application Status</TableCell>
                          <TableCell >Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredApplications.map((application, index) => (
                          <TableRow key={application.id || index} hover>
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Avatar
                                  src={application.customer?.profile_picture?.file_url}
                                  alt={application.customer?.first_name}
                                  sx={{ mr: 2 }}
                                />
                                <Box>
                                  <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 500 }}
                                  >
                                    {application.customer.first_name}&nbsp;
                                    {application.customer.last_name}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {application.customer.email}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2">
                                {formatDatePart(application.createdAt)}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {formatTimePart(application.createdAt)}
                              </Typography>
                            </TableCell>


                            <TableCell>
                              <FormControl sx={{ minWidth: 200 }}>
                                <Select
                                  value={application.status}
                                  onChange={(e) => updateApplicationStatus(application._id, e.target.value)}
                                  disabled={loading}
                                  sx={{ '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 0 }, height: 40 }}
                                  MenuProps={{ PaperProps: { sx: { '& .MuiMenuItem-root': { display: 'flex', alignItems: 'center', gap: 1 } } } }}
                                >
                                  {statusOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Circle size={16} style={{ color: option.color, fill: option.color }} />
                                        {option.label}
                                      </Box>
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>



                            <TableCell >
                              <Button
                                variant="outlined"
                                size="small"
                                sx={{ borderRadius: 2 }}
                                onClick={() => handleReviewClick(application)}
                              >
                                Review
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Modal Component to show the form */}
      {openModal && selectedApplication && (
        <LoanApplicationForm
          open={openModal}
          handleClose={() => setOpenModal(false)}
          applicationData={selectedApplication}
          twoFactorEnabled={twoFactorEnabled}
        />
      )}
    </>
  );
};

export default LoanApplications;
